import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, MoreHorizontal, X } from "lucide-react";
import { Button } from "./ui/button";
import ActionSheet from "./mobile/action-sheet";
import MobileIconButton from "./mobile/mobile-icon-button";
import { useIsMobile } from "../hooks/use-is-mobile";
import { cn } from "../lib/utils";

type SidePanelLayer = "base" | "overlay";

interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  /** Desktop width classes, e.g. "w-[900px]" or "w-full md:w-[720px]". */
  width: string;
  /** "overlay" stacks above a "base" panel (e.g. a print note opened from a details drawer). */
  layer?: SidePanelLayer;
  className?: string;
  children: React.ReactNode;
}

const DESKTOP_LAYERS: Record<SidePanelLayer, { backdrop: string; panel: string }> = {
  base: { backdrop: "z-40", panel: "z-50" },
  overlay: { backdrop: "z-50", panel: "z-[60]" },
};

const MOBILE_LAYERS: Record<SidePanelLayer, string> = {
  base: "z-[60]",
  overlay: "z-[70]",
};

/**
 * Right-hand drawer on desktop; a full-screen pushed page on phones.
 * Children should be the panel body (usually a `flex h-full flex-col` with a PanelHeader).
 */
const SidePanel: React.FC<SidePanelProps> = ({
  open,
  onClose,
  width,
  layer = "base",
  className,
  children,
}) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open || !isMobile) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isMobile, onClose]);

  if (isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            key="side-panel-mobile"
            role="dialog"
            aria-modal="true"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 40 }}
            className={cn(
              "fixed inset-0 flex flex-col overflow-hidden bg-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-2xl",
              MOBILE_LAYERS[layer],
              className,
            )}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  const layers = DESKTOP_LAYERS[layer];

  return (
    <AnimatePresence mode="wait">
      {open && (
        <>
          <motion.div
            key="side-panel-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={cn("fixed inset-0 bg-black/30", layers.backdrop)}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            key="side-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
            className={cn(
              "fixed inset-y-0 right-0 bg-white border-l border-gray-200 shadow-xl rounded-l-2xl overflow-hidden",
              layers.panel,
              width,
              className,
            )}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export interface PanelHeaderAction {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  /** Desktop button variant; defaults to "outline". */
  variant?: "default" | "outline";
  /** On phones, show this action as a button in the bar instead of the overflow sheet. */
  pinned?: boolean;
  /** Shown in red in the phone action sheet. */
  destructive?: boolean;
}

interface PanelHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: Array<PanelHeaderAction | false | null | undefined>;
  onClose: () => void;
  /** Extra desktop-only content placed before the action buttons. */
  desktopExtra?: React.ReactNode;
}

/**
 * Standard panel header. Desktop: title, action buttons and a close button in one row.
 * Phones: back chevron, title and a pinned action, with the rest in an action sheet.
 */
export const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  subtitle,
  actions,
  onClose,
  desktopExtra,
}) => {
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);
  const visibleActions = (actions ?? []).filter(
    (action): action is PanelHeaderAction => !!action,
  );

  if (isMobile) {
    const pinned = visibleActions.filter((action) => action.pinned);
    const overflow = visibleActions.filter((action) => !action.pinned);

    return (
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-gray-100 bg-white px-2">
        <MobileIconButton label="Back" variant="plain" onClick={onClose}>
          <ChevronLeft className="h-6 w-6" />
        </MobileIconButton>
        <div className="min-w-0 flex-1 px-1">
          <h2 className="truncate text-[15px] font-semibold leading-tight text-gray-900 font-jakarta">
            {title}
          </h2>
          {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
        </div>
        {pinned.map((action) => (
          <button
            key={action.label}
            type="button"
            disabled={action.disabled}
            onClick={action.onClick}
            className={cn(
              "h-9 shrink-0 rounded-full border-0 px-3.5 text-sm font-semibold disabled:opacity-40",
              action.variant === "default"
                ? "bg-primary-600 text-white active:bg-primary-700"
                : "bg-gray-100 text-gray-900 active:bg-gray-200",
            )}
          >
            {action.label}
          </button>
        ))}
        {overflow.length > 0 && (
          <>
            <MobileIconButton label="More actions" variant="plain" onClick={() => setMoreOpen(true)}>
              <MoreHorizontal className="h-5 w-5" />
            </MobileIconButton>
            <ActionSheet
              open={moreOpen}
              onClose={() => setMoreOpen(false)}
              title={typeof title === "string" ? title : undefined}
              actions={overflow.map((action) => ({
                label: action.label,
                icon: action.icon,
                onClick: action.onClick,
                disabled: action.disabled,
                destructive: action.destructive,
              }))}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
      <div className="flex items-center gap-3">
        <div className="mr-auto min-w-0">
          <h2 className="text-lg font-semibold text-foreground font-jakarta">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {desktopExtra}
        {visibleActions.map((action) => (
          <Button
            key={action.label}
            variant={action.variant ?? "outline"}
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {action.icon && <action.icon className="mr-2 h-4 w-4" />}
            {action.label}
          </Button>
        ))}
        <Button variant="outline" className="h-8 w-8 p-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default SidePanel;
